import { Translate } from 'next-translate';
import { NsbFlavorsPrecomputes } from '@api/SmartBaselineFlavorsApi';

export interface FlavorDef {
  confKey: keyof NsbFlavorsPrecomputes;
  // i18n key for the one-line "what it is" shown in the (i) tooltip next to each option in the method modal.
  description: string;
  fullLabel: string;
  id: string;
  label: string;
  netConfKey: keyof NsbFlavorsPrecomputes;
  priceKey: keyof NsbFlavorsPrecomputes;
  weight: number;
}

// ── Flavor display labels — FRONTEND-ONLY, editable here ──────────────────────────────────────
// `label`/`fullLabel`/`description` are i18n KEYS, not literal text — resolve with `t()` (or
// `translateFlavorLabel` below) at the point of render, not when reading from this array. `label` is
// the SHORT abbreviation shown ONLY on the line-item grid cell links. `fullLabel` is the FULL name shown
// EVERYWHERE ELSE (toolbar selectors, the per-cell quick-picker popup, the method modal, and the
// deep-dive Intel modal's left nav / body / detail pages) — per UX, abbreviations are confined to the
// grid. The backend/API is unchanged — it keys flavors by `id` (F1–F8) and `priceKey`, never by either
// label. This array is the single source of truth for both.
export const FLAVOR_DEFS: FlavorDef[] = [
  {
    confKey: 'conf_score_last_price',
    description: 'tlc_smart_baseline.flavors.f1.description',
    fullLabel: 'tlc_smart_baseline.flavors.f1.full_label',
    id: 'F1',
    label: 'tlc_smart_baseline.flavors.f1.label',
    netConfKey: 'net_conf_score_last_price',
    priceKey: 'last_price',
    weight: 0.50,
  },
  {
    confKey: 'conf_score_avg_recent_price',
    description: 'tlc_smart_baseline.flavors.f2.description',
    fullLabel: 'tlc_smart_baseline.flavors.f2.full_label',
    id: 'F2',
    label: 'tlc_smart_baseline.flavors.f2.label',
    netConfKey: 'net_conf_score_avg_recent_price',
    priceKey: 'avg_recent_price',
    weight: 0.65,
  },
  {
    confKey: 'conf_score_hist',
    description: 'tlc_smart_baseline.flavors.f3.description',
    fullLabel: 'tlc_smart_baseline.flavors.f3.full_label',
    id: 'F3',
    label: 'tlc_smart_baseline.flavors.f3.label',
    netConfKey: 'net_conf_score_hist',
    priceKey: 'hist_avg_price',
    weight: 0.75,
  },
  {
    confKey: 'conf_score_hist',
    description: 'tlc_smart_baseline.flavors.f3_5.description',
    fullLabel: 'tlc_smart_baseline.flavors.f3_5.full_label',
    id: 'F3.5',
    label: 'tlc_smart_baseline.flavors.f3_5.label',
    netConfKey: 'net_conf_score_hist',
    priceKey: 'hist_min_price',
    weight: 0.75,
  },
  {
    confKey: 'conf_score_hist',
    description: 'tlc_smart_baseline.flavors.f4.description',
    fullLabel: 'tlc_smart_baseline.flavors.f4.full_label',
    id: 'F4',
    label: 'tlc_smart_baseline.flavors.f4.label',
    netConfKey: 'net_conf_score_hist',
    priceKey: 'hist_p25_price',
    weight: 0.75,
  },
  {
    confKey: 'conf_score_hist',
    description: 'tlc_smart_baseline.flavors.f5.description',
    fullLabel: 'tlc_smart_baseline.flavors.f5.full_label',
    id: 'F5',
    label: 'tlc_smart_baseline.flavors.f5.label',
    netConfKey: 'net_conf_score_hist',
    priceKey: 'hist_p75_price',
    weight: 0.75,
  },
  {
    confKey: 'conf_score_trend',
    description: 'tlc_smart_baseline.flavors.f6.description',
    fullLabel: 'tlc_smart_baseline.flavors.f6.full_label',
    id: 'F6',
    label: 'tlc_smart_baseline.flavors.f6.label',
    netConfKey: 'net_conf_score_trend',
    priceKey: 'trend_projected_price',
    weight: 0.85,
  },
  {
    confKey: 'conf_score_qty_adjusted',
    description: 'tlc_smart_baseline.flavors.f7.description',
    fullLabel: 'tlc_smart_baseline.flavors.f7.full_label',
    id: 'F7',
    label: 'tlc_smart_baseline.flavors.f7.label',
    netConfKey: 'net_conf_score_qty_adjusted',
    priceKey: 'qty_adjusted_price',
    weight: 0.90,
  },
  {
    confKey: 'conf_score_fred',
    description: 'tlc_smart_baseline.flavors.f8.description',
    fullLabel: 'tlc_smart_baseline.flavors.f8.full_label',
    id: 'F8',
    label: 'tlc_smart_baseline.flavors.f8.label',
    netConfKey: 'net_conf_score_fred',
    priceKey: 'fred_adjusted_price',
    weight: 1.00,
  },
];

// Label keys for the price sources that are NOT smart-baseline flavors but share the same
// `flavorLabel` slot on the applied-state stores.
//
// These are i18n KEYS, never rendered text. `flavorLabel` doubles as the sentinel several callers
// compare against (manual-entry detection, the built-baseline recap link), so storing rendered text
// makes those comparisons locale-dependent: they pass in English by coincidence and silently stop
// matching under any other locale. Comparing keys removes that failure mode entirely.
export const MANUAL_ENTRY_LABEL_KEY = 'line_items.manual_entry';
export const BUILT_BASELINE_LABEL_KEY = 'build_baseline.built_baseline_label';
export const ICO_LABEL_KEY = 'bid_request.line_items.nsb_price_selectors.method_intelligent_counter_offer';
// Shown when the recommended flavor resolves to no FlavorDef — deliberately distinct from
// `recommended.full_label` ("Recommended"), which names the event-level choice rather than the
// applied result.
export const RECOMMENDED_APPLIED_LABEL_KEY = 'tlc_smart_baseline.flavors.recommended.applied_label';

// `flavorLabel` fields threaded through the applied-state stores hold a FlavorDef `label`/`fullLabel`
// key (from this array) or one of the source keys above. Anything else is passed through unchanged so
// a caller that hands over a plain string still renders something rather than a raw key.
const FLAVOR_LABEL_KEY_PREFIX = 'tlc_smart_baseline.flavors.';
const SOURCE_LABEL_KEYS: ReadonlySet<string> = new Set([
  MANUAL_ENTRY_LABEL_KEY,
  BUILT_BASELINE_LABEL_KEY,
  ICO_LABEL_KEY,
]);
export const translateFlavorLabel = (t: Translate, label: string): string => (
  label.startsWith(FLAVOR_LABEL_KEY_PREFIX) || SOURCE_LABEL_KEYS.has(label) ? t(label) : label
);
